// frontend/modules/service-metrics/components/TopServicesTable.tsx
import React from 'react';
import { Card, Table, Tag, Progress, Typography, Tooltip } from 'antd';
import { 
    CheckCircleOutlined, 
    WarningOutlined, 
    CloseCircleOutlined,
    ClockCircleOutlined 
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import type { ServiceMetricDTO } from '@/shared/api/model';

const { Text } = Typography;

interface TopServicesTableProps {
    data: ServiceMetricDTO[];
    loading?: boolean;
    title?: string;
    onServiceClick?: (serviceName: string) => void;
}

const TopServicesTable: React.FC<TopServicesTableProps> = ({ 
    data, 
    loading,
    title = 'Top Services',
    onServiceClick
}) => {
    const { dark } = useTheme();

    const getSuccessRateStatus = (rate: number) => {
        if (rate >= 95) return { color: 'success', icon: <CheckCircleOutlined /> };
        if (rate >= 80) return { color: 'warning', icon: <WarningOutlined /> };
        return { color: 'error', icon: <CloseCircleOutlined /> };
    };

    const columns = [
        {
            title: 'Service',
            dataIndex: 'serviceName',
            key: 'serviceName',
            render: (name: string) => (
                <a onClick={() => onServiceClick?.(name)} style={{ cursor: 'pointer' }}>
                    {name}
                </a>
            ),
        },
        {
            title: 'Total Requests',
            dataIndex: 'totalRequests',
            key: 'totalRequests',
            sorter: (a: ServiceMetricDTO, b: ServiceMetricDTO) => (a.totalRequests ?? 0) - (b.totalRequests ?? 0),
            render: (value: number) => value.toLocaleString(),
        },
        {
            title: 'Success Rate',
            dataIndex: 'successRate',
            key: 'successRate',
            sorter: (a: ServiceMetricDTO, b: ServiceMetricDTO) => (a.successRate ?? 0) - (b.successRate ?? 0),
            render: (rate: number) => {
                const status = getSuccessRateStatus(rate);
                return (
                    <Tooltip title={`${rate.toFixed(2)}%`}>
                        <Progress 
                            percent={rate} 
                            size="small" 
                            status={status.color as any}
                            format={() => `${rate.toFixed(1)}%`}
                        />
                    </Tooltip>
                );
            },
        },
        {
            title: 'Avg Response',
            dataIndex: 'averageResponseTimeMs',
            key: 'averageResponseTimeMs',
            sorter: (a: ServiceMetricDTO, b: ServiceMetricDTO) => 
                (a.averageResponseTimeMs ?? 0) - (b.averageResponseTimeMs ?? 0),
            render: (value: number) => (
                <span>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {value.toFixed(0)}ms
                </span>
            ),
        },
        {
            title: 'Failed',
            dataIndex: 'failedRequests',
            key: 'failedRequests',
            sorter: (a: ServiceMetricDTO, b: ServiceMetricDTO) => (a.failedRequests ?? 0) - (b.failedRequests ?? 0),
            render: (value: number) => (
                <Text type={value > 0 ? 'danger' : undefined}>
                    {value.toLocaleString()}
                </Text>
            ),
        },
        {
            title: 'Rate Limited',
            dataIndex: 'rateLimitedRequests',
            key: 'rateLimitedRequests',
            render: (value: number) => (
                value > 0 ? (
                    <Tag color="warning">{value.toLocaleString()}</Tag>
                ) : (
                    <Text type="secondary">0</Text>
                )
            ),
        },
        {
            title: 'CB Trips',
            dataIndex: 'circuitBreakerTrips',
            key: 'circuitBreakerTrips',
            render: (value: number) => (
                value > 0 ? (
                    <Tag color="error">{value}</Tag>
                ) : (
                    <Text type="secondary">0</Text>
                )
            ),
        },
    ];

    return (
        <Card 
            title={title} 
            loading={loading}
            className={dark ? 'bg-gray-800' : ''}
        >
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
            />
        </Card>
    );
};

export default TopServicesTable;
